import { c as _c } from "react/compiler-runtime";
import { useAuthContext } from '@/hooks/use-auth';
import { useProjectsContext } from '@/providers/projects-provider';
import { useAppNavigation } from '@/domains/navigation/use-app-navigation';
import { useQueryClient } from '@tanstack/react-query';
import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { PARAMS_KEY_ORGANIZATION_ID, PARAMS_KEY_PROJECT_ID } from '@/constants/route-params-keys';
import { ChevronsUpDown, LogOut, Plus, Search } from 'lucide-react';
import { Button } from '@/comps/primitives/button';
import { DialogConfirm } from '@/comps/dialog-confirm';
import { HeadingWithSeparator } from '@/comps/userIntelligence/chart/listing-items/ChartItemsListHeader';
import { WithClassName, WithDisabled, WithOnChange } from '@/types/component';
import { cn } from '@/utils/shad';
import { capitalize } from 'lodash';
import { Popover, PopoverContent, PopoverTrigger } from '@/comps/primitives/popover';
import type { PopperContentProps } from '@radix-ui/react-popper';
import { MemoizedProjectItem } from '@/domains/dashboard/components/ProjectItem';
import { RandomHueCircle, getHueCircleStyleFromSeed, type HueCircleStyle } from '@/domains/dashboard/components/RandomHueCircle';
import { OrganizationModel, ProjectModel, ProjectProduct } from '@/models/organizations/internal-models';
import { CreateOrganizationModal, CreateOrganizationResp } from '@/domains/dashboard/components/CreateOrganizationModal';
import { CreateProjectModal } from '@/domains/dashboard/components/CreateProjectModal';
import { QUERY_KEY_ORGANIZATION_AGGREGATES, QUERY_KEY_ORGANIZATION_PROJECTS, QUERY_KEY_ORGANIZATIONS } from '@/constants/query-keys';
import { useLocalStorageState } from '@/hooks/useSessionState';
import { useGlobalFiltersContext } from '@/contexts/filters/filter-context';
import { useLogout } from '@/hooks/use-logout';
type ProjectSelectRenderTriggerProps = {
  open: boolean;
  disabled: boolean;
  projectName: string;
  projectProduct: ProjectProduct;
  nReports: number;
  organizationHueStyle?: HueCircleStyle;
};
type ProjectSelectProps = WithClassName & WithDisabled & {
  renderTrigger?: (props: ProjectSelectRenderTriggerProps) => React.ReactElement;
  popoverSide?: PopperContentProps['side'];
  popoverAlign?: PopperContentProps['align'];
};
export type ProjectSelection = {
  organization_id: string;
  project_id: string;
  tenant?: string;
};
export const ProjectSelect = ({
  className,
  disabled,
  renderTrigger,
  popoverSide,
  popoverAlign = 'start'
}: ProjectSelectProps) => {
  const {
    isNebulyAdmin,
    isCompanyAdmin,
    canWrite
  } = useAuthContext();
  const isAdmin = isCompanyAdmin || isNebulyAdmin;
  const {
    isSharedLink,
    tenant
  } = useAuthContext();
  const {
    selectedProject,
    setProjectId,
    clearChartFilters,
    setOrganizationId,
    organizations,
    selectedOrganization
  } = useProjectsContext();
  const {
    navigateApp,
    goToHomeWithCleanQueryParams
  } = useAppNavigation();
  const {
    setFilters: setGlobalFilters
  } = useGlobalFiltersContext();
  const client = useQueryClient();
  const [searchProject, setSearchProject] = useState<string>('');
  const [searchOrganization, setSearchOrganization] = useState<string>('');
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [_, setLastSelectedProjects] = useLocalStorageState<ProjectSelection | null>('lastSelectedProjects', null);
  const organizationsItems: TSelectItem<OrganizationModel>[] = useMemo(() => {
    return organizations?.map(org => {
      return mapOrganizationToSelectItem(org);
    }).filter(item => item.name.toLowerCase().includes(searchOrganization.toLowerCase())) || [];
  }, [organizations, searchOrganization]);
  const selectedOrganizationItem = useMemo(() => {
    return selectedOrganization ? organizationsItems.find(item_0 => item_0.id === selectedOrganization.id) : null;
  }, [organizationsItems, selectedOrganization]);
  const projectsItems: TSelectItem<ProjectModel>[] = useMemo(() => {
    return selectedOrganization?.projects?.map(mapProjectToSelectItem).filter(item_1 => item_1.name.toLowerCase().includes(searchProject.toLowerCase())) || [];
  }, [searchProject, selectedOrganization?.projects]);
  const {
    logout
  } = useLogout();
  const handlePopoverOpenChange = useCallback((open: boolean) => {
    setPopoverOpen(open);
  }, []);

  // ===============================================
  // EFFECTS
  // ===============================================

  // ===============================================
  // FUNCTIONS
  // ===============================================
  const onNewProjectCreated = useCallback(async (createdProjectId: string) => {
    try {
      if (!selectedOrganization?.id) return;
      await Promise.all([client.invalidateQueries({
        queryKey: [QUERY_KEY_ORGANIZATIONS]
      }), client.invalidateQueries({
        queryKey: [QUERY_KEY_ORGANIZATION_PROJECTS]
      })]);
      goToHomeWithCleanQueryParams(createdProjectId, selectedOrganization.id);
    } catch (error) {
      console.error('Error handling new project creation:', error);
    }
  }, [client, goToHomeWithCleanQueryParams, selectedOrganization?.id]);
  const onNewOrganizationCreated = useCallback(async ({
    organizationId,
    projectId
  }: CreateOrganizationResp) => {
    try {
      await Promise.all([client.invalidateQueries({
        queryKey: [QUERY_KEY_ORGANIZATION_AGGREGATES]
      }), client.invalidateQueries({
        queryKey: [QUERY_KEY_ORGANIZATION_PROJECTS]
      }), client.invalidateQueries({
        queryKey: [QUERY_KEY_ORGANIZATIONS]
      })]);
      goToHomeWithCleanQueryParams(projectId, organizationId);

      // autoselect the new organization and project
      await Promise.all([setOrganizationId(organizationId), setProjectId(projectId)]);
      handlePopoverOpenChange(false);
    } catch (error_0) {
      console.error('Error handling new organization creation:', error_0);
    }
  }, [client, goToHomeWithCleanQueryParams, setOrganizationId, setProjectId, handlePopoverOpenChange]);
  const goToOrganizationsPage = useCallback(() => {
    navigateApp({
      route: 'settings-section',
      section: 'organizations',
      keepList: [PARAMS_KEY_ORGANIZATION_ID, PARAMS_KEY_PROJECT_ID]
    });
    handlePopoverOpenChange(false);
  }, [navigateApp, handlePopoverOpenChange]);
  const goToProjectsPage = useCallback(() => {
    navigateApp({
      route: 'settings-section',
      section: 'projects',
      keepList: [PARAMS_KEY_ORGANIZATION_ID, PARAMS_KEY_PROJECT_ID]
    });
    handlePopoverOpenChange(false);
  }, [navigateApp, handlePopoverOpenChange]);
  const canSelectProject = useCallback(() => {
    return !!selectedOrganization?.id;
  }, [selectedOrganization?.id]);
  const selectOrganization = useCallback(async (id: string) => {
    if (isSelecting) return; // Prevent multiple selections
    setIsSelecting(true);
    try {
      const firstProjectId = organizations.find(elem => elem.id === id)?.projects[0]?.id;
      if (!firstProjectId) throw new Error('No project found for organization');
      setLastSelectedProjects({
        organization_id: id,
        project_id: firstProjectId,
        tenant: tenant
      });
      setGlobalFilters([]);
      goToHomeWithCleanQueryParams(firstProjectId, id);
      handlePopoverOpenChange(false);
    } catch (error_1) {
      console.error('Error selecting organization:', error_1);
      // optionally show error to user via toast/alert
    } finally {
      setIsSelecting(false);
    }
  }, [isSelecting, organizations, setLastSelectedProjects, tenant, setGlobalFilters, goToHomeWithCleanQueryParams, handlePopoverOpenChange]);
  const selectProject = useCallback(async (id_0: string) => {
    if (isSelecting) return; // Prevent multiple selections
    setIsSelecting(true);
    try {
      if (!canSelectProject()) {
        throw new Error('Required states not set for navigation');
      }
      // Do not call setProjectId or _setReportId before goToHome: nuqs would rewrite /explore and break the page before navigation.
      if (id_0 !== selectedProject?.id) {
        clearChartFilters();
      }
      setLastSelectedProjects({
        organization_id: selectedOrganization?.id as string,
        project_id: id_0,
        tenant: tenant
      });
      setGlobalFilters([]);
      goToHomeWithCleanQueryParams(id_0, selectedOrganization?.id as string);
      handlePopoverOpenChange(false);
    } catch (error_2) {
      console.error('Error selecting project:', error_2);
      // optionally show error to user via toast/alert
    } finally {
      setIsSelecting(false);
    }
  }, [isSelecting, clearChartFilters, selectedProject?.id, canSelectProject, setLastSelectedProjects, selectedOrganization?.id, tenant, setGlobalFilters, goToHomeWithCleanQueryParams, handlePopoverOpenChange]);
  // ===============================================
  // JSX
  // ===============================================
  const organizationHueStyle = useMemo(() => selectedOrganization?.id ? getHueCircleStyleFromSeed(selectedOrganization.id) : undefined, [selectedOrganization?.id]);
  const triggerDisabled = !!(isSharedLink || isSelecting || disabled);
  const projectName = selectedProject?.name || '(no project selected)';
  const projectProduct = selectedProject?.product ?? 'external';
  const nReports = selectedProject?.nReports ?? 0;
  const triggerProps: ProjectSelectRenderTriggerProps = {
    open: popoverOpen,
    disabled: triggerDisabled,
    projectName,
    projectProduct,
    nReports,
    organizationHueStyle
  };
  const triggerElement = renderTrigger ? renderTrigger(triggerProps) : <Button variant="ghost" className="justify-star flex w-full items-center justify-between text-[11px]" AppendIcon={ChevronsUpDown} disabled={triggerDisabled} title={selectedProject?.name}>
      <span className="flex items-center gap-1.5">
        {selectedOrganizationItem?.icon}
        <span className="mr-1.5 max-w-24.75 min-w-0 truncate overflow-hidden">{projectName}</span>
      </span>
    </Button>;
  return <div className={className}>
      <Popover onOpenChange={handlePopoverOpenChange} open={popoverOpen}>
        <PopoverTrigger asChild>{triggerElement}</PopoverTrigger>
        <PopoverContent className="w-auto bg-background shadow-neutral-900" align={popoverAlign} side={popoverSide} onCloseAutoFocus={event => event.preventDefault()}>
          <div className="flex border-b border-border">
              {/* === ORGANIZATIONS LIST === */}
              <ProjectSelectSection showColumnDivider title="organization" searchValue={searchOrganization} onSearchChange={e => setSearchOrganization(e.currentTarget.value)} onClickShowAll={goToOrganizationsPage} listElement={<ProjectItemsList items={organizationsItems} variant="picker" onSelect={item_2 => {
            selectOrganization(item_2.id);
          }} selectedItemId={selectedOrganization?.id} />} showCreateButton={isAdmin} createButton={<CreateOrganizationModal onConfirm={onNewOrganizationCreated}>
                    <Button variant="outline" PrependIcon={Plus}>
                      Create organization
                    </Button>
                  </CreateOrganizationModal>} />

              {/* === PROJECTS LIST === */}
              <ProjectSelectSection key={selectedOrganization?.id} title="project" searchValue={searchProject} onSearchChange={e_0 => setSearchProject(e_0.currentTarget.value)} onClickShowAll={goToProjectsPage} listElement={<ProjectItemsList items={projectsItems} variant="picker" onSelect={item_3 => {
            selectProject(item_3.id);
          }} selectedItemId={selectedProject?.id} />} showCreateButton={canWrite} createButton={<CreateProjectModal organizationId={selectedOrganization?.id ?? ''} onConfirm={onNewProjectCreated}>
                    <Button variant="outline" PrependIcon={Plus}>
                      Create project
                    </Button>
                  </CreateProjectModal>} />
          </div>
          <div className="flex items-center justify-end gap-2 p-2 px-3 text-muted-foreground">
            <p>Logout</p>
            <DialogConfirm description="Are you sure you want to exit from the platform?" title="Logout" onConfirm={logout} confirmTestId="rail-logout-confirm">
              <Button variant="outline" className="size-8" aria-label="Logout" data-testid="rail-logout-button">
                <LogOut className="size-3" />
              </Button>
            </DialogConfirm>
          </div>
        </PopoverContent>
      </Popover>
    </div>;
};
function mapProjectToSelectItem(project: ProjectModel): TSelectItem<ProjectModel> {
  return {
    id: project.id,
    name: project.name,
    rawData: project
  };
}
function mapOrganizationToSelectItem(org: OrganizationModel): TSelectItem<OrganizationModel> {
  const iconStyle = getHueCircleStyleFromSeed(org.id);
  return {
    id: org.id,
    name: org.name,
    icon: <RandomHueCircle {...iconStyle} />,
    iconStyle,
    rawData: org
  };
}
export type TSelectItem<TData = unknown> = {
  id: string;
  name: string;
  icon?: React.ReactNode;
  iconStyle?: HueCircleStyle;
  rawData?: TData;
};
type ProjectSelectSectionProps = {
  title: string;
  createButton: React.ReactNode;
  onClickShowAll?: () => void;
  onSearchChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  searchValue: string;
  listElement?: React.ReactNode;
  hideShowAll?: boolean;
  showCreateButton?: boolean;
  showColumnDivider?: boolean;
};
function ProjectSelectSection(t0) {
  const $ = _c(27);
  const {
    createButton,
    title,
    onClickShowAll,
    onSearchChange,
    searchValue,
    listElement,
    hideShowAll,
    showCreateButton,
    showColumnDivider: t1
  } = t0;
  const showColumnDivider = t1 === undefined ? false : t1;
  const t2 = showColumnDivider && "border-r border-border";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("flex w-60 shrink-0 flex-col overflow-hidden pt-3.5", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = `header for ${title}`;
  const t5 = `Search ${title}`;
  let t6;
  if ($[2] !== onSearchChange || $[3] !== searchValue || $[4] !== t5) {
    t6 = <div className="flex w-full grow gap-1.5 pr-1.5 pb-1.5"><SearchInput className="grow" placeholder={t5} value={searchValue} onChange={onSearchChange} /></div>;
    $[2] = onSearchChange;
    $[3] = searchValue;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== hideShowAll || $[7] !== onClickShowAll) {
    t7 = !hideShowAll && <button className="pr-1.5 text-[11px] text-muted-foreground hover:cursor-pointer hover:underline" onClick={onClickShowAll}>see all</button>;
    $[6] = hideShowAll;
    $[7] = onClickShowAll;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== title) {
    t8 = capitalize(title);
    $[9] = title;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t7 || $[12] !== t8) {
    t9 = <HeadingWithSeparator headingLevel="h1" headerClassName="text-[11px]" appendChildren={t7}>{t8}s</HeadingWithSeparator>;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t4 || $[15] !== t6 || $[16] !== t9) {
    t10 = <header aria-label={t4} className="pl-2.5">{t6}{t9}</header>;
    $[14] = t4;
    $[15] = t6;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="grow" />;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== createButton || $[20] !== showCreateButton) {
    t12 = showCreateButton && <div className="border-t px-2.5 py-1.5">{createButton}</div>;
    $[19] = createButton;
    $[20] = showCreateButton;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== listElement || $[23] !== t10 || $[24] !== t12 || $[25] !== t3) {
    t13 = <section className={t3}>{t10}{listElement}{t11}{t12}</section>;
    $[22] = listElement;
    $[23] = t10;
    $[24] = t12;
    $[25] = t3;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
}
type ProjectItemsProps<TData> = {
  items: TSelectItem<TData>[];
  selectedItemId?: string;
  onSelect: (item: TSelectItem<TData>) => void;
  variant?: 'default' | 'picker';
};
function ProjectItemsList(t0) {
  const $ = _c(10);
  const {
    items,
    onSelect,
    selectedItemId,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const t2 = variant === "picker" ? "flex flex-col gap-1 px-2 pb-2.5" : "py-2.5 pr-3.5 pl-1.5";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("max-h-[45vh] overflow-y-auto", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== items || $[3] !== onSelect || $[4] !== selectedItemId || $[5] !== variant) {
    t4 = items?.map(item => <li key={item.id} className="min-w-0"><MemoizedProjectItem item={item} variant={variant} onSelect={item_0 => {
        onSelect(item_0);
      }} selected={item.id === selectedItemId} /></li>);
    $[2] = items;
    $[3] = onSelect;
    $[4] = selectedItemId;
    $[5] = variant;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <ul className={t3}>{t4}</ul>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
type SearchInputProps = WithClassName & WithOnChange<ChangeEvent<HTMLInputElement>> & {
  placeholder?: string;
  value: string;
};
function SearchInput(t0) {
  const $ = _c(10);
  const {
    placeholder,
    value,
    className,
    onChange
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex items-center gap-1.5", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Search size={9} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onChange || $[4] !== placeholder || $[5] !== value) {
    t3 = <input tabIndex={-1} aria-label="Search" value={value} onChange={onChange} className="w-full bg-transparent placeholder:text-[11px] placeholder:text-neutral-500 placeholder:italic" type="search" placeholder={placeholder} />;
    $[3] = onChange;
    $[4] = placeholder;
    $[5] = value;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <div className={t1}>{t2}{t3}</div>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}